import { useState } from 'react';
import ItemCard from './ItemCard';
import DecisionModal from './DecisionModal';
const logo = '/simmer-logo-cropped.png';

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeLeft(expiresAt) {
  const ms = Math.max(0, expiresAt - Date.now());
  const d  = Math.floor(ms / 864e5);
  const h  = Math.floor((ms % 864e5) / 36e5);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

function daysLeft(expiresAt) {
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 864e5));
}

// ── Featured card (first simmering item) ──────────────────────────────────────
function FeaturedCard({ item, onArchive, onDetails }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 rounded-xl overflow-hidden bg-white shadow-sm mb-4">
      {/* Image — left half on desktop, full-width on mobile */}
      <div className="relative overflow-hidden bg-surface-low group min-h-[220px]">
        {item.image && !imgErr ? (
          <img
            src={item.image}
            alt={item.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-5xl text-outline-var">
            ✦
          </div>
        )}
      </div>

      {/* Content — right half */}
      <div className="p-6 md:p-8 flex flex-col justify-between bg-white">
        <div>
          <p className="font-body text-[0.6rem] font-semibold tracking-[0.2em] uppercase text-outline mb-2">
            Patience Remaining
          </p>
          <p className="font-headline italic text-5xl text-primary leading-none mb-4">
            {timeLeft(item.expiresAt)}
          </p>
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-headline italic text-xl text-on-surface leading-snug hover:text-primary transition-colors duration-300 block mb-2"
          >
            {item.name}
          </a>
          {item.price && (
            <p className="font-body text-sm font-semibold text-primary">${item.price.toFixed(2)}</p>
          )}
          {item.siteName && (
            <p className="font-body text-xs text-outline mt-1">{item.siteName}</p>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-6">
          <button
            onClick={onArchive}
            className="w-full py-3 rounded-lg font-body text-[0.72rem] font-bold uppercase tracking-widest text-white transition-opacity duration-300 hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #994212, #b85929)' }}
          >
            Move to Archive
          </button>
          <button
            onClick={onDetails}
            className="w-full py-2 font-body text-[0.68rem] uppercase tracking-widest text-outline hover:text-on-surface transition-colors duration-300"
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Secondary grid card ───────────────────────────────────────────────────────
function GridCard({ item, onDelete }) {
  const [imgErr, setImgErr] = useState(false);
  const days = daysLeft(item.expiresAt);

  return (
    <div className="rounded-xl overflow-hidden bg-white shadow-sm group flex flex-col">
      {/* Image — takes ~65% of card height via padding-top trick */}
      <div className="relative overflow-hidden bg-surface-low" style={{ paddingTop: '90%' }}>
        {item.image && !imgErr ? (
          <img
            src={item.image}
            alt={item.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-3xl text-outline-var">
            ✦
          </div>
        )}

        {/* Price — top right */}
        {item.price && (
          <span className="absolute top-2 right-2 font-body text-[0.62rem] font-bold text-primary bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 leading-none">
            ${item.price.toFixed(2)}
          </span>
        )}

        {/* Delete — top left, appears on hover */}
        <button
          onClick={() => onDelete(item.id)}
          className="absolute top-2 left-2 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-outline hover:text-primary transition-colors duration-200 opacity-0 group-hover:opacity-100 text-sm leading-none"
          aria-label="Remove item"
        >
          ×
        </button>
      </div>

      {/* Details */}
      <div className="p-3 flex flex-col flex-1">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-headline italic text-[0.88rem] text-on-surface leading-snug hover:text-primary transition-colors duration-200 line-clamp-2 mb-auto"
        >
          {item.name}
        </a>

        {/* Bottom row */}
        <div className="flex items-end justify-between mt-2 gap-1">
          <span
            className="font-body text-[0.6rem] font-bold uppercase tracking-wide text-white rounded-full px-2 py-0.5 flex-shrink-0"
            style={{ background: '#994212' }}
          >
            {days}d left
          </span>
          {item.siteName && (
            <span className="font-body text-[0.58rem] text-outline truncate text-right leading-tight">
              {item.siteName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add new reflection card ───────────────────────────────────────────────────
function AddCard({ onAddItem }) {
  return (
    <button
      onClick={onAddItem}
      className="rounded-xl border-2 border-dashed border-outline-var flex flex-col items-center justify-center gap-2 text-outline hover:border-primary hover:text-primary transition-colors duration-300 w-full"
      style={{ minHeight: 160 }}
    >
      <span className="text-2xl leading-none font-light">+</span>
      <span className="font-body text-[0.62rem] font-semibold uppercase tracking-widest">
        New Reflection
      </span>
    </button>
  );
}

// ── Main WishList ─────────────────────────────────────────────────────────────
export default function WishList({ waitingItems, readyItems, onDecide, onDelete, onSkipTimer, onAddItem }) {
  const [pendingDecision, setPendingDecision] = useState(null);

  const handleIntent = (id, decision) => {
    const item = [...readyItems, ...waitingItems].find(i => i.id === id);
    if (item) setPendingDecision({ item, decision });
  };

  const handleDone = () => {
    if (pendingDecision) onDecide(pendingDecision.item.id, pendingDecision.decision);
    setPendingDecision(null);
  };

  if (waitingItems.length === 0 && readyItems.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon"><img src={logo} alt="Simmer" className="empty-logo" /></div>
        <h2>Nothing simmering yet</h2>
        <p>Tap <strong>+ Add something to simmer</strong> below to start.</p>
      </div>
    );
  }

  const [featured, ...rest] = waitingItems;

  return (
    <>
      {/* ── Ready to Decide — unchanged ── */}
      {readyItems.length > 0 && (
        <section className="section">
          <div className="section-header section-header--ready">
            <h2>Ready to Decide</h2>
            <span className="section-count">{readyItems.length}</span>
          </div>
          <div className="cards">
            {readyItems.map(item => (
              <ItemCard
                key={item.id}
                item={item}
                onIntent={handleIntent}
                onDelete={onDelete}
                isReady
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Bento Simmering grid ── */}
      {waitingItems.length > 0 && (
        <section>
          {/* Hero text */}
          <div className="mb-6">
            <h1 className="font-headline italic text-4xl text-on-surface leading-tight">
              The Curated Simmer.
            </h1>
            <p className="font-body text-sm text-outline mt-1">
              Items you've set aside to breathe.
            </p>
          </div>

          {/* Featured card */}
          <FeaturedCard
            item={featured}
            onArchive={() => handleIntent(featured.id, 'skipped')}
            onDetails={() => window.open(featured.url, '_blank')}
          />

          {/* Secondary grid + Add card */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {rest.map(item => (
              <GridCard key={item.id} item={item} onDelete={onDelete} />
            ))}
            <AddCard onAddItem={onAddItem} />
          </div>
        </section>
      )}

      <p className="list-footer-hint">More items you're simmering on will appear here</p>

      {pendingDecision && (
        <DecisionModal
          item={pendingDecision.item}
          decision={pendingDecision.decision}
          onDone={handleDone}
        />
      )}
    </>
  );
}
